/**
 * @fileoverview Loading placeholder for a sales table (Active Clients, My Page, Sales Pipeline,
 * Sales Team, User Management, and the two Sales Performance tables all share this shape).
 * @description Renders a header bar plus `rows` placeholder rows, each split into `columns` cells
 * with the given relative widths — approximating the real table's column proportions so the page
 * doesn't jump when data replaces it. Wrapped in the same white/bordered card shell the real
 * tables use, so it can drop straight into a page's pending branch.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Rahul Chawda
 */

import Skeleton from '@/shared/components/atoms/Skeleton'

interface TableSkeletonProps {
  columns?: number
  rows?: number
}

/**
 * @component
 * @param {TableSkeletonProps} props - How many placeholder columns and rows to render.
 * @param {number} [props.columns] - Column count. Defaults to 5.
 * @param {number} [props.rows] - Row count. Defaults to 6.
 */
function TableSkeleton({ columns = 5, rows = 6 }: TableSkeletonProps) {
  const columnWidths = Array.from({ length: columns }, (_, index) => (index === 0 ? '70%' : '55%'))

  return (
    <section className="rounded-[10px] border border-divider bg-white">
      <div className="flex flex-wrap items-center gap-2.5 p-3.5 sm:p-4">
        <Skeleton width={260} height={38} />
        <Skeleton width={140} height={38} />
        <Skeleton width={140} height={38} />
      </div>
      <div className="border-t border-divider">
        {Array.from({ length: rows }, (_, rowIndex) => (
          <div
            key={rowIndex}
            className="grid items-center gap-3 border-b border-row-border px-4 py-3.5 last:border-b-0 sm:px-5"
            style={{ gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))` }}
          >
            {columnWidths.map((width, colIndex) => (
              <Skeleton key={colIndex} width={width} height={13} />
            ))}
          </div>
        ))}
      </div>
    </section>
  )
}

export default TableSkeleton
